import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Facebook, Instagram } from 'lucide-react';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'About', path: '/about' },
  { name: 'Book Now', path: '/contact' },
  { name: 'My Account', path: '/account' },
];

const socialLinks = [
  { 
    name: 'Facebook', 
    icon: Facebook, 
    url: 'https://facebook.com/share/18TvPyzdbo/?mibextid=wwXIfr' 
  },
  { 
    name: 'TikTok', 
    icon: () => <i className="fab fa-tiktok text-xl"></i>, 
    url: 'https://tiktok.com/@braidsbymel25' 
  },
  { 
    name: 'Instagram', 
    icon: Instagram, 
    url: 'https://www.instagram.com/reel/DVghGgEE37e/?igsh=MTAxNzgxaDRmMTh4dQ==' 
  },
  { 
    name: 'WhatsApp', 
    icon: () => <i className="fab fa-whatsapp text-xl"></i>, 
    url: 'https://wa.me/639166744137' 
  },
];

export const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const menuVariants = {
    closed: {
      opacity: 0,
      transition: { duration: 0.3 }
    },
    open: {
      opacity: 1,
      transition: { duration: 0.3 }
    }
  };

  const linkVariants = {
    closed: { opacity: 0, y: 50 },
    open: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.4, ease: [0.4, 0, 0.2, 1] }
    })
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link 
            to="/" 
            className="font-script text-3xl md:text-4xl text-gold hover:text-gold-light transition-colors"
            data-testid="logo-link"
          >
            BraidsByMel
          </Link>
          
          <button
            onClick={() => setIsMenuOpen(true)}
            className="text-gold hover:text-pink transition-colors p-2"
            data-testid="menu-toggle"
            aria-label="Open menu"
          >
            <Menu size={28} />
          </button>
        </div>
      </header>

      {/* Fullscreen Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
            className="fixed inset-0 z-50 bg-black/95 backdrop-blur-2xl flex flex-col justify-center items-center"
          >
            <button
              onClick={() => setIsMenuOpen(false)}
              className="absolute top-6 right-6 text-gold hover:text-pink transition-colors p-2"
              data-testid="menu-close"
              aria-label="Close menu"
            >
              <X size={32} />
            </button>

            <nav className="flex flex-col items-center gap-6 md:gap-8">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.path}
                  custom={i}
                  variants={linkVariants}
                  initial="closed"
                  animate="open"
                >
                  <Link
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`font-script text-5xl md:text-7xl lg:text-8xl menu-link ${
                      location.pathname === link.path ? 'text-pink' : 'text-gold'
                    }`}
                    data-testid={`menu-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
            </nav>

            {/* Social Links in Menu */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0, transition: { delay: 0.6 } }}
              className="absolute bottom-12 flex gap-8"
            >
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gold hover:text-pink transition-colors social-icon"
                  data-testid={`social-${social.name.toLowerCase()}`}
                >
                  {typeof social.icon === 'function' ? (
                    <social.icon />
                  ) : (
                    <social.icon size={24} />
                  )}
                </a>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-surface border-t border-gold/20 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12">
            {/* Brand */}
            <div>
              <h3 className="font-script text-4xl text-gold mb-4">BraidsByMel</h3>
              <p className="text-[#A3A3A3] font-sans leading-relaxed mb-4">
                The Philippines' premier Black-owned hair salon, proudly serving all nationals. 
                We specialize in authentic African braids and precision haircut designs.
              </p>
              <p className="text-[#A3A3A3] font-sans text-sm">
                5682 Doña Carmen St, Poblacion, Makati City
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-serif text-xl text-gold-light mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-[#A3A3A3] hover:text-pink transition-colors font-sans"
                      data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Social */}
            <div>
              <h4 className="font-serif text-xl text-gold-light mb-4">Follow Us</h4>
              <div className="flex gap-6">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold hover:text-pink transition-all transform hover:scale-110"
                    data-testid={`footer-social-${social.name.toLowerCase()}`}
                  >
                    {typeof social.icon === 'function' ? (
                      <social.icon />
                    ) : (
                      <social.icon size={28} />
                    )}
                  </a>
                ))}
              </div>
              <p className="text-[#A3A3A3] mt-6 font-sans">
                <a href="https://braidsbymel.online" className="text-gold hover:text-pink transition-colors">
                  BraidsByMel.online
                </a>
              </p>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gold/10 text-center">
            <p className="text-[#A3A3A3] font-sans text-sm">
              © {new Date().getFullYear()} BraidsByMel. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
